/**
 * SettingsPaymentConditionsController — onglet « Paiements & conditions »
 * de /parametres.html (EPIC-004 / 4.6).
 *
 * Pilote 3 `nm-edit-block` indépendants :
 *   - block-delai      : délai par défaut (jours, 0-90).
 *   - block-penalites  : type + valeur de pénalités de retard.
 *   - block-conditions : mention « conditions de paiement » — soit
 *     auto-générée à partir des deux blocs ci-dessus, soit personnalisée
 *     par l'utilisateur (case à cocher « Personnaliser »).
 *
 * Hydratation initiale : `GET /organization/settings` à `connect()`,
 * puis chaque sauvegarde re-hydrate via la réponse complète du PUT.
 *
 * Convention CLAUDE.md respectée :
 *   - Pas d'HTML inline en JS : tous les résumés sont rendus depuis
 *     `<template>` (`tpl-conditions-*`) clonés via cloneTemplate / fillSlots.
 *   - Aucune classe CSS inventée.
 *   - Vocabulaire UI : « jours », « taux légal », « % », identique à la
 *     maquette.
 *
 * Note : on n'utilise PAS `maquettes/shared/edit-block.js` côté production.
 * L'ouverture/fermeture des blocs (toggle summary ↔ form) est portée
 * directement par les méthodes `openBlock` / `cancelBlock` du controller.
 */

import BaseController from "./base_controller.js";
import { api } from "/js/services/api.js";

const PENALTY_TYPE_LABELS = {
  multiple_taux_legal: (v) => `${v}× le taux légal`,
  fixe: (v) => `${v} % annuel`,
  bce_plus_pts: (v) => `BCE refi + ${v} pts`,
};

const PENALTY_PHRASES = {
  multiple_taux_legal: (v) => `à ${v} fois le taux d'intérêt légal en vigueur`,
  fixe: (v) => `à un taux fixe annuel de ${v} %`,
  bce_plus_pts: (v) => `au taux BCE refi majoré de ${v} points`,
};

export default class SettingsPaymentConditionsController extends BaseController {
  static targets = [
    "blockDelai", "blockPenalites", "blockConditions",
    "delaiSummary", "delaiInput", "delaiSubmit",
    "penalitesSummary", "penaltyTypeInput", "penaltyValueInput", "penalitesSubmit",
    "conditionsSummary", "customCheckbox", "conditionsTextarea", "conditionsSubmit",
  ];

  // État serveur — la dernière réponse `paymentConditions`. Sert de
  // référence pour reconstituer la phrase auto-générée et pour les
  // sauvegardes partielles (savePaymentDelay n'envoie que le délai).
  state = {
    paymentDelayDefaultDays: 30,
    paymentConditionsText: null,
    penaltyRateType: "multiple_taux_legal",
    penaltyRateValue: 3,
  };

  async connect() {
    try {
      const data = await api.getSettings();
      if (data && data.paymentConditions) {
        this._hydrateFromState(data.paymentConditions);
      }
    } catch (_error) {
      // 401 déjà redirigé par api.js. Pour toute autre erreur, on garde
      // les défauts de la maquette et on prévient l'utilisateur.
      this.showError("Impossible de charger les conditions de paiement.");
    }
  }

  // ---------------------------------------------------------------------------
  // edit-block : open / cancel
  // ---------------------------------------------------------------------------

  openBlock(event) {
    event.preventDefault();
    const block = event.currentTarget.closest("[data-edit-block]");
    if (!block) return;
    this._open(block);
  }

  cancelBlock(event) {
    event.preventDefault();
    const block = event.currentTarget.closest("[data-edit-block]");
    if (!block) return;
    // Restaurer les inputs depuis l'état serveur courant.
    this._populateInputs(this.state);
    this._close(block);
  }

  // ---------------------------------------------------------------------------
  // Hydration UI
  // ---------------------------------------------------------------------------

  _hydrateFromState(paymentConditions) {
    this.state = {
      paymentDelayDefaultDays: Number(paymentConditions.paymentDelayDefaultDays ?? 30),
      paymentConditionsText: paymentConditions.paymentConditionsText ?? null,
      penaltyRateType: String(paymentConditions.penaltyRateType ?? "multiple_taux_legal"),
      penaltyRateValue: Number(paymentConditions.penaltyRateValue ?? 3),
    };
    this._populateInputs(this.state);
    this._renderDelaiSummary();
    this._renderPenalitesSummary();
    this._renderConditionsSummary();
  }

  _populateInputs(state) {
    if (this.hasDelaiInputTarget) {
      this.delaiInputTarget.value = String(state.paymentDelayDefaultDays);
    }
    if (this.hasPenaltyTypeInputTarget) {
      this.penaltyTypeInputTarget.value = state.penaltyRateType;
    }
    if (this.hasPenaltyValueInputTarget) {
      this.penaltyValueInputTarget.value = String(state.penaltyRateValue);
    }
    if (this.hasCustomCheckboxTarget) {
      const isCustom = state.paymentConditionsText !== null;
      this.customCheckboxTarget.checked = isCustom;
      if (this.hasConditionsTextareaTarget) {
        if (isCustom) {
          this.conditionsTextareaTarget.value = state.paymentConditionsText;
          this.conditionsTextareaTarget.removeAttribute("readonly");
        } else {
          this.conditionsTextareaTarget.value = this._buildAutoMention(state);
          this.conditionsTextareaTarget.setAttribute("readonly", "");
        }
      }
    }
  }

  _renderDelaiSummary() {
    if (!this.hasDelaiSummaryTarget) return;
    const fragment = this.cloneTemplate("tpl-conditions-delai-summary");
    this.fillSlots(fragment, {
      delai: `${this.state.paymentDelayDefaultDays} jours`,
    });
    this._replaceChildren(this.delaiSummaryTarget, fragment);
  }

  _renderPenalitesSummary() {
    if (!this.hasPenalitesSummaryTarget) return;
    const fragment = this.cloneTemplate("tpl-conditions-penalites-summary");
    const labelFn = PENALTY_TYPE_LABELS[this.state.penaltyRateType] ?? PENALTY_TYPE_LABELS.multiple_taux_legal;
    this.fillSlots(fragment, {
      penalty: labelFn(this.state.penaltyRateValue),
    });
    this._replaceChildren(this.penalitesSummaryTarget, fragment);
  }

  _renderConditionsSummary() {
    if (!this.hasConditionsSummaryTarget) return;
    const isCustom = this.state.paymentConditionsText !== null;
    const text = isCustom
      ? this.state.paymentConditionsText
      : this._buildAutoMention(this.state);
    const hint = isCustom
      ? "Texte personnalisé. Inséré dans le PDF Carbone et le champ EN16931 « payment terms »."
      : "Texte généré automatiquement à partir des deux blocs ci-dessus. Inséré dans le PDF Carbone et le champ EN16931 « payment terms ».";

    const fragment = this.cloneTemplate("tpl-conditions-mention-summary");
    this.fillSlots(fragment, { text, hint });
    this._replaceChildren(this.conditionsSummaryTarget, fragment);
  }

  _buildAutoMention(state) {
    const phraseFn = PENALTY_PHRASES[state.penaltyRateType] ?? PENALTY_PHRASES.multiple_taux_legal;
    const phrase = phraseFn(state.penaltyRateValue);
    return `Paiement à ${state.paymentDelayDefaultDays} jours date d'émission. Pénalités de retard égales ${phrase}. Indemnité forfaitaire pour frais de recouvrement : 40 €.`;
  }

  // ---------------------------------------------------------------------------
  // « Personnaliser le texte » (toggle readonly + auto-fill)
  // ---------------------------------------------------------------------------

  toggleCustom() {
    if (!this.hasCustomCheckboxTarget || !this.hasConditionsTextareaTarget) return;
    if (this.customCheckboxTarget.checked) {
      this.conditionsTextareaTarget.removeAttribute("readonly");
      this.conditionsTextareaTarget.focus();
    } else {
      this.conditionsTextareaTarget.value = this._buildAutoMention({
        ...this.state,
        // Lire les valeurs courantes des inputs si en cours d'édition,
        // sinon retomber sur l'état serveur.
        paymentDelayDefaultDays: this._readDelai(),
        penaltyRateType: this._readPenaltyType(),
        penaltyRateValue: this._readPenaltyValue(),
      });
      this.conditionsTextareaTarget.setAttribute("readonly", "");
    }
  }

  // ---------------------------------------------------------------------------
  // Submit handlers
  // ---------------------------------------------------------------------------

  async savePaymentDelay(event) {
    event.preventDefault();
    event.stopImmediatePropagation();
    const delai = this._readDelai();
    if (delai === null) {
      this.showError("Le délai doit être un entier entre 0 et 90.");
      return;
    }
    await this._submit(
      { paymentDelayDefaultDays: delai },
      this.hasDelaiSubmitTarget ? this.delaiSubmitTarget : null,
      this.hasBlockDelaiTarget ? this.blockDelaiTarget : null,
      "Délai de paiement enregistré.",
    );
  }

  async savePenalites(event) {
    event.preventDefault();
    event.stopImmediatePropagation();
    const type = this._readPenaltyType();
    const value = this._readPenaltyValue();
    if (type === null || value === null) {
      this.showError("Pénalités de retard invalides (valeur entre 0 et 100).");
      return;
    }
    await this._submit(
      { penaltyRateType: type, penaltyRateValue: value },
      this.hasPenalitesSubmitTarget ? this.penalitesSubmitTarget : null,
      this.hasBlockPenalitesTarget ? this.blockPenalitesTarget : null,
      "Pénalités de retard enregistrées.",
    );
  }

  async saveConditions(event) {
    event.preventDefault();
    event.stopImmediatePropagation();
    const isCustom = this.hasCustomCheckboxTarget && this.customCheckboxTarget.checked;
    const text = isCustom
      ? (this.hasConditionsTextareaTarget ? this.conditionsTextareaTarget.value : "")
      : null; // null → repasse en mode auto-généré côté backend.
    await this._submit(
      { paymentConditionsText: text },
      this.hasConditionsSubmitTarget ? this.conditionsSubmitTarget : null,
      this.hasBlockConditionsTarget ? this.blockConditionsTarget : null,
      "Mention conditions de paiement enregistrée.",
    );
  }

  // ---------------------------------------------------------------------------
  // Helpers
  // ---------------------------------------------------------------------------

  _readDelai() {
    if (!this.hasDelaiInputTarget) return null;
    const raw = Number.parseInt(this.delaiInputTarget.value, 10);
    if (!Number.isFinite(raw) || raw < 0 || raw > 90) return null;
    return raw;
  }

  _readPenaltyType() {
    if (!this.hasPenaltyTypeInputTarget) return null;
    const v = this.penaltyTypeInputTarget.value;
    return ["multiple_taux_legal", "fixe", "bce_plus_pts"].includes(v) ? v : null;
  }

  _readPenaltyValue() {
    if (!this.hasPenaltyValueInputTarget) return null;
    const raw = Number.parseFloat(this.penaltyValueInputTarget.value);
    if (!Number.isFinite(raw) || raw < 0 || raw > 100) return null;
    return raw;
  }

  async _submit(payload, submitButton, block, successMessage) {
    if (submitButton) submitButton.disabled = true;
    try {
      const data = await api.updatePaymentConditions(payload);
      if (data && data.paymentConditions) {
        this._hydrateFromState(data.paymentConditions);
      }
      if (block) this._close(block);
      this.showSuccess(successMessage);
    } catch (error) {
      const msg = error && error.message
        ? error.message
        : "Erreur lors de la mise à jour des conditions de paiement.";
      this.showError(msg);
    } finally {
      if (submitButton) submitButton.disabled = false;
    }
  }

  _open(block) {
    block.dataset.editState = "editing";
    const summary = block.querySelector("[data-edit-summary]");
    const form = block.querySelector("[data-edit-form]");
    if (summary) summary.hidden = true;
    if (form) {
      form.hidden = false;
      const first = form.querySelector("input:not([type='hidden']), select, textarea");
      if (first) first.focus();
    }
  }

  _close(block) {
    block.dataset.editState = "idle";
    const summary = block.querySelector("[data-edit-summary]");
    const form = block.querySelector("[data-edit-form]");
    if (form) form.hidden = true;
    if (summary) summary.hidden = false;
  }

  _replaceChildren(target, fragment) {
    while (target.firstChild) target.removeChild(target.firstChild);
    target.appendChild(fragment);
  }
}
